import { useState, useEffect, useCallback } from 'react';
import { getChatHistory, setChatHistory, removeChatHistory, setDynamicSystemPrompt } from '../services/sessionStorageService';
import logger from '../utils/logger';

const useChatHistory = () => {
  // React state is used only for UI reactivity
  // sessionStorage remains the definitive source of truth
  const [conversationHistory, setConversationHistory] = useState([]);

  // Load history from sessionStorage on mount and sync with React state
  const loadFromSessionStorage = useCallback(() => {
    const stored = getChatHistory();
    setConversationHistory(stored);
    return stored;
  }, []);

  // Initialize on mount
  useEffect(() => {
    loadFromSessionStorage();
  }, [loadFromSessionStorage]);

  // Add message function that updates sessionStorage first, then React state
  const addMessage = useCallback((message) => {
    // Get current history from sessionStorage (source of truth)
    const currentHistory = getChatHistory();
    const updatedHistory = [...currentHistory, message];
    
    // Fix: Resilient message persistence during component lifecycle
    // Update sessionStorage first (always works, even if component unmounted)
    setChatHistory(updatedHistory);
    
    // Then update React state for UI reactivity (only if component still mounted)
    // Fix: Handle edge case where API response arrives after component unmounts
    // (e.g., user navigates away before response arrives)
    try {
      setConversationHistory(updatedHistory);
    } catch (error) {
      // Component may be unmounted - sessionStorage update still succeeded
      logger.info('Component unmounted during addMessage, but sessionStorage updated successfully');
    }
    
    return updatedHistory;
  }, []);

  // Initialize chat with system prompt
  const initializeWithSystemPrompt = useCallback((systemPromptData) => {
    const systemMessage = {
      role: 'system',
      content: systemPromptData.prompt,
      timestamp: new Date().toISOString(),
      metadata: {
        subject: systemPromptData.subject,
        category: systemPromptData.category,
        version: systemPromptData.version,
        last_updated: systemPromptData.last_updated
      }
    };

    // Store dynamic system prompt separately for future reference
    setDynamicSystemPrompt(systemPromptData);
    
    // Check if chat history already has a system prompt
    const currentHistory = getChatHistory();
    if (currentHistory.length === 0 || currentHistory[0].role !== 'system') {
      const newHistory = [systemMessage];
      setChatHistory(newHistory);
      setConversationHistory(newHistory);
      return newHistory;
    }
    
    return currentHistory;
  }, []);

  // Clear history function that clears sessionStorage first, then React state
  const clearHistory = useCallback(() => {
    // Clear sessionStorage first (source of truth)
    removeChatHistory();
    
    // Then update React state for UI reactivity
    setConversationHistory([]);
  }, []);

  // Get current history from sessionStorage (always current)
  const getCurrentHistory = useCallback(() => {
    return getChatHistory();
  }, []);

  // Force refresh from sessionStorage
  const refreshFromStorage = useCallback(() => {
    return loadFromSessionStorage();
  }, [loadFromSessionStorage]);

  // Set entire conversation history
  const setConversationHistoryFromStorage = useCallback((history) => {
    setChatHistory(history);
    setConversationHistory(history);
  }, []);

  return {
    conversationHistory, // React state for UI reactivity
    addMessage,
    clearHistory,
    initializeWithSystemPrompt,
    getCurrentHistory, // Always gets from sessionStorage
    refreshFromStorage,
    setConversationHistory: setConversationHistoryFromStorage
  };
};

export default useChatHistory;
